import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ASSOC } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { Loader2, User, CheckCircle2, Sparkles, Camera } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Inscription() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address: "", birth_date: "", photo_base64: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Photo trop lourde (max 2 Mo)"); return; }
    const r = new FileReader();
    r.onload = () => setForm((s) => ({ ...s, photo_base64: r.result }));
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("Prénom et nom requis"); return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error("Indiquez au moins un email OU un téléphone"); return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/public/register-member`, form);
      setDone(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center p-6">
        <div className="bg-white border border-brand-border rounded-xl shadow-sm p-10 max-w-lg w-full text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-brand-green/10 flex items-center justify-center mb-5">
            <CheckCircle2 className="text-brand-green" size={42} />
          </div>
          <h1 className="font-serif text-3xl text-brand-navy mb-2">Inscription enregistrée !</h1>
          <p className="text-gray-600 leading-relaxed mb-4">
            Bienvenue {form.first_name} parmi les futur(e)s membres de l'association{" "}
            <strong>ACTION IMPACT JEUNESSE</strong>.
          </p>
          <div className="bg-brand-bg border border-brand-border rounded-lg p-4 text-sm text-gray-700 mb-5">
            <div className="small-caps">N° d'adhérent provisoire</div>
            <div className="font-mono text-brand-navy text-base mt-1">{done.member_number}</div>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            Votre inscription sera validée par le bureau de l'association sous peu. Vous serez recontacté(e) via les coordonnées que vous avez fournies.
          </p>
          <Button asChild className="bg-brand-orange hover:bg-[#D04A06] text-white">
            <a href={`https://${ASSOC.website}`} target="_blank" rel="noreferrer">
              Visiter le site de l'association
            </a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg">
      {/* Header */}
      <header className="bg-brand-navy text-white">
        <div className="max-w-3xl mx-auto px-6 py-8 flex items-center gap-4">
          <img src={ASSOC.logo} alt="Logo" className="w-14 h-14 bg-white rounded-full p-1 object-contain" />
          <div>
            <div className="small-caps text-white/60">Bulletin d'adhésion</div>
            <h1 className="font-serif text-3xl leading-tight">Action Impact Jeunesse</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-brand-orange mb-1">
            <Sparkles size={14} />
            <span className="small-caps">Rejoignez-nous</span>
          </div>
          <h2 className="font-serif text-4xl text-brand-navy mb-2">Devenir membre</h2>
          <p className="text-gray-600 leading-relaxed">
            Remplissez le formulaire ci-dessous pour rejoindre l'association.
            Vos informations sont transmises directement au bureau qui validera votre inscription.
          </p>
        </div>

        <form onSubmit={submit} data-testid="public-register-form" className="bg-white border border-brand-border rounded-xl p-6 md:p-8 space-y-5">
          {/* Photo */}
          <div className="flex items-center gap-4 pb-5 border-b border-brand-border">
            <div className="w-24 h-24 rounded-full border-2 border-dashed border-brand-border overflow-hidden bg-gray-50 flex items-center justify-center shrink-0">
              {form.photo_base64 ? (
                <img src={form.photo_base64} alt="" className="w-full h-full object-cover" />
              ) : (
                <User size={32} className="text-gray-400" />
              )}
            </div>
            <div className="flex-1">
              <Label className="flex items-center gap-2 mb-1">
                <Camera size={14} /> Votre photo (recommandé)
              </Label>
              <Input data-testid="public-photo-input" type="file" accept="image/*" onChange={onPhoto} />
              <p className="text-xs text-gray-400 mt-1">JPG ou PNG — 2 Mo max. Sera utilisée sur votre carte de membre.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Prénom *</Label><Input data-testid="public-first-name" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} required /></div>
            <div><Label>Nom *</Label><Input data-testid="public-last-name" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} required /></div>
            <div><Label>Email</Label><Input data-testid="public-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="vous@exemple.fr" /></div>
            <div><Label>Téléphone</Label><Input data-testid="public-phone" type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="06 ..." /></div>
            <div className="md:col-span-2"><Label>Adresse postale</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
            <div><Label>Date de naissance</Label><Input type="date" value={form.birth_date || ""} onChange={(e) => setForm({...form, birth_date: e.target.value})} /></div>
          </div>

          <div>
            <Label>Quelques mots sur vous (facultatif)</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              placeholder="Vos motivations, vos disponibilités, vos compétences à mettre au service de l'association..."
            />
          </div>

          <p className="text-xs text-gray-400">
            * Champs obligatoires. Email <em>ou</em> téléphone requis pour que nous puissions vous recontacter.
            Vos données sont uniquement utilisées dans le cadre de la gestion de l'association.
          </p>

          <Button
            type="submit"
            data-testid="public-register-submit"
            disabled={loading}
            className="w-full h-12 bg-brand-orange hover:bg-[#D04A06] text-white text-base"
          >
            {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
            Envoyer mon inscription
          </Button>

          <div className="text-center pt-2 border-t border-brand-border">
            <p className="text-xs text-gray-500">
              Vous préférez vous engager comme bénévole ?{" "}
              <Link to="/inscription-benevole" className="text-brand-orange hover:underline">
                Devenir bénévole
              </Link>
            </p>
          </div>
        </form>

        <footer className="mt-10 text-center text-xs text-gray-400 leading-relaxed">
          {ASSOC.name} — Association loi 1901<br />
          {ASSOC.address}<br />
          {ASSOC.email} · {ASSOC.website}
        </footer>
      </main>

      <Toaster position="top-right" richColors />
    </div>
  );
}
